<script setup lang="ts">
import type {
  AlertDialogContentEmits,
  AlertDialogContentProps,
} from "reka-ui"
import type { HTMLAttributes } from "vue"
import {
  AlertDialogContent,
  AlertDialogPortal,
  injectDialogRootContext,
  useForwardPropsEmits,
} from "reka-ui"
import { computed } from "vue"
import { cn } from "@/shared/lib/utils"
import AlertDialogOverlay from "./AlertDialogOverlay.vue"

/**
 * Контент модального вікна. Reka бере на себе focus-trap, повернення фокуса на
 * тригер, Esc-close, scroll-lock тіла та `role="alertdialog"`. Backdrop-click reka
 * за семантикою alert-dialog НЕ закриває (захист від випадкового дисмісу
 * незворотної дії) — вмикається опціонально через `overlayClose`.
 */
const props = withDefaults(
  defineProps<
    AlertDialogContentProps & {
      class?: HTMLAttributes["class"]
      /** Клас для scrim-overlay (рідко потрібно перевизначати). */
      overlayClass?: HTMLAttributes["class"]
      /** Дозволити закриття кліком по backdrop (default: false — alert-семантика). */
      overlayClose?: boolean
    }
  >(),
  { overlayClose: false },
)
const emits = defineEmits<AlertDialogContentEmits>()

const delegatedProps = computed(() => {
  const {
    class: _class,
    overlayClass: _overlayClass,
    overlayClose: _overlayClose,
    ...rest
  } = props
  return rest
})
const forwarded = useForwardPropsEmits(delegatedProps, emits)

const rootContext = injectDialogRootContext()

function onOverlayClick(): void {
  if (props.overlayClose) {
    rootContext.onOpenChange(false)
  }
}
</script>

<template>
  <AlertDialogPortal>
    <AlertDialogOverlay
      :class="props.overlayClass"
      @click="onOverlayClick"
    />
    <AlertDialogContent
      data-slot="alert-dialog-content"
      v-bind="forwarded"
      :class="
        cn(
          'bg-popover text-popover-foreground fixed top-1/2 left-1/2 z-50 grid w-[calc(100%-2rem)] max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 gap-4 rounded-lg border p-6 shadow-lg sm:max-w-lg',
          props.class,
        )
      "
    >
      <slot />
    </AlertDialogContent>
  </AlertDialogPortal>
</template>
